import {
  createContext,
  Children,
  cloneElement,
  useContext,
  useEffect,
  useRef,
  forwardRef,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import {
  useTabState as useReaTabState,
  Tab as ReaTab,
  TabList as ReaTabList,
  TabPanel as ReaTabPanel,
} from 'reakit/Tab';
import styled from 'styled-components';

import { Select } from '@components/select';
import { useIsMobile } from '@hooks/use-is-mobile';

const TabStateContext = createContext();

const TabList = observer(
  forwardRef((props, ref) => {
    const {
      label,
      children,
      state,
      variant = 'base',
      border = false,
      onSelect,
      collapseOnMobile = false,
      ...rest
    } = props;
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    if (isMobile && collapseOnMobile) {
      const childrenItems = Children.map(children, (child, index) => {
        if (!child) return null;
        return { label: child.props.children, value: String(index), link: child.props.link };
      });

      return (
        <TabStateContext.Provider value={{ state, variant }}>
          <div ref={ref}>
            <Select
              isClearable={false}
              isSearchable={false}
              onChange={(idx) => {
                state.select(idx);
                onSelect && onSelect(Number(idx));
                const link = childrenItems[idx].link;
                link && navigate(link);
              }}
              defaultValue={state.selectedId}
              options={childrenItems}
            />
          </div>
        </TabStateContext.Provider>
      );
    }

    const childrenWithIndexedStops = Children.map(children, (child, index) => {
      if (!child) return null;
      return cloneElement(child, { ...child.props, stopId: String(index), onSelect });
    });

    return (
      <TabStateContext.Provider value={{ state, variant }}>
        <div ref={ref}>
          <StyledTabList {...rest} {...state} variant={variant} aria-label={label}>
            {childrenWithIndexedStops}
            {border && <Border />}
          </StyledTabList>
        </div>
      </TabStateContext.Provider>
    );
  })
);

const Border = styled.div((p) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '0.0625rem',
  backgroundColor: p.theme.colors.purpleTrans10,
}));

const TabPanelList = observer(({ state, children }) => {
  const newChildren = Children.map(children, (child, index) => {
    if (!child) return null;
    return cloneElement(child, { ...child.props, stopId: String(index) });
  });

  return <TabStateContext.Provider value={{ state }}>{newChildren}</TabStateContext.Provider>;
});

const TabPanel = observer(({ stopId, ...rest }) => {
  const { state } = useContext(TabStateContext);
  if (stopId !== state.selectedId) return null;
  return <StyledTabPanel tabId={stopId} {...state} {...rest} />;
});

const StyledTabPanel = styled(ReaTabPanel)((p) => ({
  outline: 'none',
}));

const Tab = observer(({ children, stopId, onSelect, link, ...rest }) => {
  const { state, variant } = useContext(TabStateContext);
  const isThisTabSelected = stopId === state.selectedId;

  const selectTab = (index) => {
    state.select(index);
    if (onSelect) {
      onSelect(Number(index));
    }
  };

  if (variant === 'pill') {
    return (
      <PillTabContainer
        active={isThisTabSelected}
        id={stopId}
        {...state}
        select={selectTab}
        {...rest}
        data-tab-item
      >
        <span>{children}</span>
      </PillTabContainer>
    );
  }
  return (
    <TabContainer
      active={isThisTabSelected}
      id={stopId}
      {...state}
      select={selectTab}
      {...rest}
      data-tab-item
    >
      {link ? <Link to={link}>{children}</Link> : <span>{children}</span>}
      <TabActiveBorder visible={isThisTabSelected} />
    </TabContainer>
  );
});

const useTabState = ({ selectedId, ...args } = {}) => {
  const realId = selectedId === null || selectedId === undefined ? selectedId : String(selectedId);
  const tabState = useReaTabState({ selectedId: realId, ...args });
  const selected = useRef(realId);

  useEffect(() => {
    if (realId !== undefined && selected.current !== realId) {
      selected.current = realId;
      tabState.setSelectedId(realId);
    }
  }, [realId, tabState]);

  return tabState;
};

export { useTabState, TabList, TabPanelList, TabPanel, Tab };

// @STYLES
const StyledTabList = styled(({ variant, ...rest }) => <ReaTabList {...rest} />)((p) => ({
  outline: 'none',
  position: 'relative',
  display: 'flex',
  'button:not(:first-of-type)': {
    '&[data-tab-item]': {
      marginLeft: p.variant === 'pill' ? p.theme.spacing.m8 : p.theme.spacing.m24,
    },
  },
}));

const TabActiveBorder = styled.div((p) => ({
  transition: p.visible ? 'all 150ms ease-out' : 'all 150ms ease-out',
  backgroundColor: p.theme.colors.purple100,
  position: 'absolute',
  bottom: 0,
  transform: p.visible ? 'scaleX(1) translateX(0px)' : 'scaleX(0) translateX(50%)',
  width: '100%',
  height: '0.25rem',
  zIndex: 2,
}));

const TabContainer = styled(({ active, ...rest }) => <ReaTab {...rest} />)((p) => ({
  height: '2.5rem',
  position: 'relative',
  ...p.theme.text.semibold14,
  transition: 'color 150ms ease-out',
  color: p.disabled
    ? p.theme.colors.purpleTrans20
    : p.active
    ? p.theme.colors.purple100
    : p.theme.colors.purple60,
  zIndex: 2,
  'span, a': {
    display: 'block',
    outline: 'none',
    height: '2.5rem',
    ':focus': {
      color: p.theme.colors.purple100,
    },
  },
  ':focus': {
    outline: 'none',
  },
  display: 'flex',
  ...(!p.active &&
    !p.disabled && {
      ':hover, :focus': {
        color: p.theme.colors.purple100,
        cursor: 'pointer',
      },
    }),
}));

const PillTabContainer = styled(({ active, ...rest }) => <ReaTab {...rest} />)((p) => ({
  display: 'inline-block',
  padding: '0.625rem 1.5rem',
  transition: 'all 150ms ease',
  borderRadius: '20px',
  ...p.theme.text.semibold14,
  backgroundColor: p.theme.colors.purpleTrans5,
  color: p.theme.colors.purple80,
  ...(!p.active &&
    !p.disabled && {
      ':hover, :focus': {
        cursor: 'pointer',
        backgroundColor: p.theme.colors.purpleTrans10,
      },
    }),
  ...(p.active && {
    color: p.theme.colors.white100,
    backgroundColor: p.theme.colors.purple100,
  }),
}));
